import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";

import PDFViewer, { ISignatureField } from "components/PDFViewer";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import KeysContext from "contexts/KeysContext";

import pluginService, { ISignFieldAppearance } from "api/pluginService";
import generateLettersService from "api/generateLettersService";
import { axiosInstance } from "api/axios";
import { IRequestCreateOutGoingParcel, Receiver } from "api/parcelService";

import usePDF from "hooks/usePDF";
import usePlugin from "hooks/usePlugin";
import { ItemKind } from "dto/IKindItems";
import { ICertificate } from "dto/ICertificate";
import { IKey } from "dto/IKey";
import { EParcelType } from "dto/EParcelType";
import { ENotificationType, notification } from "utils/notification";
import { letterPublicKeyFor } from "utils/ebiLetterKeys";

import styles from "./ownSign.module.scss";

// Kanały zewnętrzne — wtyczka wgrywa podpisany PDF plaintext (odbiorca poza Notą).
const PLAINTEXT_CHANNELS: EParcelType[] = [
  EParcelType.Email,
  EParcelType.ExternalEDelivery,
  EParcelType.Puh,
];

const receiverIdOf = (r: Receiver) => String(r.Id ?? "");

interface Props {
  documentId: string;
  documentDate?: string | null;
  /** Plik pisma bazowego (projektu) do podglądu — `SignedPdfUri ?? FileUri`. */
  baseFileUri?: string | null;
  baseFileName?: string | null;
  baseEncryptionKeyId?: string | null;
  /** Payload generacji (adresaci + skład + data), zbudowany przez baseGeneration(false). */
  gen: IRequestCreateOutGoingParcel;
  onCancel: () => void;
  /** Zwraca payload z podpisanymi URI odbiorców + ReceiversPreSigned — do createParcel. */
  onSigned: (signedGen: IRequestCreateOutGoingParcel) => void;
}

type Phase = "cert" | "loading" | "position" | "signing" | "error";

/**
 * Własny podpis elektroniczny KAŻDEJ kopii pisma. Krok po kroku:
 *  1) wybór certyfikatu,
 *  2) podgląd pisma BAZOWEGO w PDFViewer (tryb pozycjonowania) — ustawienie znacznika
 *     podpisu; ta sama pozycja trafia na wszystkie kopie,
 *  3) pętla po odbiorcach: wtyczka renderuje→PODPISUJE→szyfruje→wgrywa każdą kopię
 *     (pasek postępu),
 *  4) zwrot payloadu z podpisanymi URI + flagą ReceiversPreSigned do createParcel.
 */
const OwnSignFlow = ({
  documentId,
  documentDate,
  baseFileUri,
  baseFileName,
  baseEncryptionKeyId,
  gen,
  onCancel,
  onSigned,
}: Props) => {
  const { keys, setKeys } = useContext(KeysContext);
  const { decryptPackage } = usePDF();
  const plugin = usePlugin();

  const { data: certsData, isLoading: certsLoading } =
    pluginService.useGetCerificates();
  const certs = useMemo(() => certsData?.data ?? [], [certsData]);

  const [phase, setPhase] = useState<Phase>("cert");
  const [cert, setCert] = useState<ICertificate | null>(null);
  const [field, setField] = useState<ISignatureField | null>(null);
  const [baseBlob, setBaseBlob] = useState<Blob | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const keyRequested = useRef(false);
  const decrypting = useRef(false);

  const all = useMemo(
    () => [
      ...gen.Receivers.map((r) => ({ r, cc: false })),
      ...gen.Cc.map((r) => ({ r, cc: true })),
    ],
    [gen],
  );

  // (1) Klucz pisma bazowego → KeysContext (jak DocumentViewModule), żeby móc
  // odszyfrować/wyświetlić podgląd niezależnie od miejsca uruchomienia.
  useEffect(() => {
    if (keyRequested.current) return;
    if (!baseFileUri || !baseEncryptionKeyId) {
      setErrorMsg(
        "Brak pliku pisma bazowego do podglądu — nie można ustawić pozycji podpisu.",
      );
      setPhase("error");
      return;
    }
    keyRequested.current = true;
    (async () => {
      try {
        const res = await axiosInstance.get(
          `/keys/object?id=${baseEncryptionKeyId}`,
        );
        // Serwer dokłada ParentPrivateKey/ParentKeyId poza kształtem IKey.
        const key = res.data as IKey & {
          ParentPrivateKey?: string | null;
          ParentKeyId?: string | null;
        };
        const parentKey: IKey | null = key?.ParentPrivateKey
          ? {
              Id: key.ParentKeyId ?? "",
              ParentKeyId: null,
              PrivateKey: key.ParentPrivateKey,
              PublicKey: "",
              Initial: false,
              Armored: false,
            }
          : null;
        setKeys(
          [...keys, key, parentKey].filter(Boolean) as IKey[],
        );
      } catch {
        setErrorMsg("Nie udało się pobrać klucza pisma bazowego.");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFileUri, baseEncryptionKeyId]);

  // (2) Podgląd pisma bazowego (odszyfrowanie / render docx→PDF przez wtyczkę),
  // gdy klucz jest już w kontekście.
  useEffect(() => {
    if (baseBlob || decrypting.current) return;
    if (!baseFileUri || !baseEncryptionKeyId) return;
    if (!keys.some((k) => k?.Id === baseEncryptionKeyId)) return;
    decrypting.current = true;
    decryptPackage({
      FileName: baseFileName ?? "pismo.pdf",
      EncryptionKeyId: baseEncryptionKeyId,
      Uri: baseFileUri,
      Kind: ItemKind.EditorDocument,
    })
      .then((b) => setBaseBlob(b))
      .catch(() => {
        setErrorMsg(
          "Nie udało się przygotować podglądu pisma bazowego (sprawdź, czy wtyczka Nota jest uruchomiona).",
        );
        setPhase("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, baseBlob]);

  // Gdy podgląd dojechał już po wyborze certyfikatu — przejdź do pozycjonowania.
  useEffect(() => {
    if (phase === "loading" && baseBlob) setPhase("position");
  }, [phase, baseBlob]);

  const handleChooseCert = (c: ICertificate) => {
    setCert(c);
    setPhase(baseBlob ? "position" : "loading");
  };

  const fieldToAppearance = (f: ISignatureField): ISignFieldAppearance => ({
    X: f.x,
    Y: f.y,
    Width: f.width,
    Height: f.height,
    Page: f.page > 0 ? f.page : 1,
    PageWidth: f.pwidth,
    PageHeight: f.pheight,
  });

  const runSigning = async (
    chosenCert: ICertificate,
    chosenField: ISignatureField,
  ) => {
    setPhase("signing");
    setErrorMsg("");
    setProgress({ done: 0, total: all.length });
    try {
      // (a) BE: mapy zmiennych DOCVARIABLE per odbiorca.
      const prepRes = await generateLettersService.prepareContent({
        DocumentId: documentId,
        DocumentDate: documentDate,
        Receivers: all.map(({ r, cc }) => ({
          ReceiverId: receiverIdOf(r),
          Id: r.Id ?? null,
          Type: (r.Type as number) ?? 0,
          Email: r.Email,
          Cc: cc,
        })),
      });
      const byId = new Map(
        (prepRes.data.Receivers ?? []).map((p) => [p.ReceiverId, p]),
      );

      const appearance = fieldToAppearance(chosenField);

      // (b) JEDNO wywołanie wtyczki: render + PODPIS całej partii przy tych samych
      // współrzędnych (batch-PIN — karta pyta o PIN raz), szyfrowanie i upload.
      await plugin.ensureConnected();
      const genRes = await pluginService.generateLetters({
        DocumentId: documentId,
        Kind: ItemKind.EditorDocument,
        Receivers: all.map(({ r }) => {
          const p = byId.get(receiverIdOf(r));
          return {
            ReceiverId: receiverIdOf(r),
            // pusty = klucz dokumentu (zachowany oryginał autora); adresat EBI — klucz jego pisma
            PublicKey: letterPublicKeyFor(r),
            Variables: p?.Variables ?? {},
            RemoveParagraphsForVariables: p?.RemoveParagraphsForVariables ?? [],
            Plaintext: PLAINTEXT_CHANNELS.includes(r.Parcel),
          };
        }),
        CertificateSerialNumber: chosenCert.SerialNumber,
        FieldAppearance: appearance,
      });

      const results = genRes.data.Results ?? [];
      const uriById = new Map<string, string>(
        results
          .filter((x) => x.Success && x.Uri)
          .map((x) => [x.ReceiverId, x.Uri as string]),
      );

      const missing = all.filter(({ r }) => !uriById.get(receiverIdOf(r)));
      if (missing.length > 0) {
        const firstErr = results.find((x) => !x.Success)?.Error;
        throw new Error(
          firstErr ?? "Nie udało się podpisać wszystkich kopii pism.",
        );
      }
      setProgress({ done: all.length, total: all.length });

      // (c) Podmień URI odbiorców na PODPISANE kopie + oznacz ReceiversPreSigned.
      const withUri = (r: Receiver): Receiver => ({
        ...r,
        Uri: uriById.get(receiverIdOf(r)) ?? r.Uri,
      });
      onSigned({
        ...gen,
        ReceiversPreSigned: true,
        Receivers: gen.Receivers.map(withUri),
        Cc: gen.Cc.map(withUri),
      });
    } catch (e) {
      setErrorMsg(
        (e as Error)?.message ??
          "Nie udało się podpisać pism. Upewnij się, że wtyczka Nota jest uruchomiona i połączona.",
      );
      setPhase("error");
    }
  };

  const handleConfirmField = (f: ISignatureField | null) => {
    if (!f) {
      notification({
        type: ENotificationType.INFO,
        title: "Ustaw znacznik podpisu",
        text: "Kliknij na piśmie, aby umieścić znacznik podpisu, a następnie zatwierdź.",
      });
      return;
    }
    if (!cert) return;
    setField(f);
    runSigning(cert, f);
  };

  return ReactDOM.createPortal(
    <div className={styles.overlay} onMouseDown={(e) => e.stopPropagation()}>
      <div className={styles.panel}>
        <div className={styles.head}>
          <div className={styles.title}>
            Własny podpis elektroniczny — podpisywanie kopii
          </div>
          <HollowButton text="Anuluj" onClick={onCancel} />
        </div>

        {phase === "cert" && (
          <div className={styles.body}>
            <div className={styles.stepTitle}>Wybierz certyfikat podpisu</div>
            {certsLoading ? (
              <div className={styles.center}>
                <Loading />
                <div className={styles.hint}>Odczyt certyfikatów z wtyczki…</div>
              </div>
            ) : certs.length === 0 ? (
              <div className={styles.empty}>
                Brak dostępnych certyfikatów. Uruchom i połącz wtyczkę Nota oraz
                podłącz kartę/certyfikat kwalifikowany, a następnie spróbuj ponownie.
              </div>
            ) : (
              <div className={styles.certList}>
                {certs.map((c) => (
                  <div
                    key={c.SerialNumber}
                    className={styles.certCard}
                    onClick={() => handleChooseCert(c)}
                  >
                    <div className={styles.certTop}>
                      <div className={styles.certName}>{c.Name}</div>
                      <div className={styles.certDate}>
                        {moment(c.ValidTo).format("DD.MM.YYYY")}
                      </div>
                    </div>
                    <div className={styles.certIssuer}>{c.IssuerBy}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {phase === "loading" && (
          <div className={styles.center}>
            <Loading />
            <div className={styles.hint}>Przygotowuję podgląd pisma bazowego…</div>
          </div>
        )}

        {phase === "position" && baseBlob && (
          <div className={styles.viewer}>
            <PDFViewer
              file={baseBlob}
              inModal
              showCloseButton={false}
              signPositioningMode
              signPositioningHint="Ustaw znacznik podpisu na piśmie bazowym — ta sama pozycja zostanie użyta na każdej wygenerowanej kopii. Kliknij, aby dodać znacznik, dopasuj położenie/rozmiar i zatwierdź."
              onConfirmSignaturePosition={handleConfirmField}
            />
          </div>
        )}

        {phase === "signing" && (
          <div className={styles.center}>
            <Loading />
            <div className={styles.hint}>
              Renderowanie i podpisywanie {progress.total}{" "}
              {progress.total === 1 ? "pisma" : "pism"}…
            </div>
            <div className={styles.subHint}>
              Jeśli pojawi się pytanie o PIN karty — wystarczy podać go raz dla
              całej partii. Nie zamykaj okna.
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className={styles.center}>
            <div className={styles.error}>{errorMsg}</div>
            <div className={styles.errBtns}>
              <HollowButton text="Zamknij" onClick={onCancel} />
              {cert && field && (
                <Button
                  text="Spróbuj ponownie"
                  onClick={() => runSigning(cert, field)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default OwnSignFlow;
